import { useState } from 'react';
import { Copy, ExternalLink, Trash2, Edit2, Check, BarChart3, QrCode, Loader2, ShieldAlert, Ban, Power } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, Badge, Button, PlatformIcon } from '@/components/ui';
import { getPlatformConfig } from '@/utils/platformDetector';
import { getShortUrl, getShortUrlWithProtocol } from '@/utils/domainResolver';
import type { Deeplink } from '@/types';

interface DeeplinkCardProps {
  deeplink: Deeplink;
  clicks?: number;
  clicksLoading?: boolean;
  onDelete: (id: string) => void;
  onToggleDisabled?: (id: string) => void;
}

const DeeplinkCard = ({ deeplink, clicks = 0, clicksLoading = false, onDelete, onToggleDisabled }: DeeplinkCardProps) => {
  const { t, i18n } = useTranslation();
  const [copied, setCopied] = useState(false);
  const platformConfig = getPlatformConfig(deeplink.platform);
  const shortUrl = getShortUrl(deeplink);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(getShortUrlWithProtocol(deeplink));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const formatDate = (timestamp: { seconds: number } | Date | null) => {
    if (!timestamp) return t('common.unknown');
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp.seconds * 1000);
    return date.toLocaleDateString(i18n.language, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <Card variant="default" padding="none" className="overflow-hidden">
      <div className="p-5">
        <div className="flex items-start gap-4">
          {/* Platform Icon */}
          <PlatformIcon platform={deeplink.platform} size="lg" />

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-dark-800 dark:text-white truncate">
                {deeplink.name || shortUrl}
              </h3>
              <Badge variant="lavender" size="sm">
                {platformConfig.name}
              </Badge>
              {deeplink.status === 'rejected' && (
                <Badge variant="error" size="sm">
                  <ShieldAlert className="w-3 h-3 mr-1" />
                  {t('deeplinks.rejected')}
                </Badge>
              )}
              {deeplink.status === 'disabled' && (
                <Badge variant="dark" size="sm">
                  <Ban className="w-3 h-3 mr-1" />
                  {t('deeplinks.disabled')}
                </Badge>
              )}
            </div>

            {/* Short URL */}
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lime-600 dark:text-lime-400 font-medium">{shortUrl}</span>
              <button
                onClick={handleCopy}
                className="p-1 text-gray-400 hover:text-lime-600 transition-colors"
                title={t('common.copyLinkTooltip')}
              >
                {copied ? (
                  <Check className="w-4 h-4 text-green-500" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </button>
            </div>

            {/* Original URL */}
            <p className="text-sm text-gray-500 dark:text-gray-400 truncate mb-3">
              {deeplink.originalUrl}
            </p>

            {/* Meta */}
            <div className="flex items-center gap-4 text-xs text-gray-400">
              <span>{t('deeplinks.created', { date: formatDate(deeplink.createdAt) })}</span>
              <span className="flex items-center gap-1">
                <BarChart3 className="w-3 h-3" />
                {clicksLoading ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  t('deeplinks.click', { count: clicks })
                )}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between px-5 py-3 bg-gray-50 dark:bg-dark-700 border-t border-gray-100 dark:border-dark-600">
        <div className="flex items-center gap-2">
          <Link to={`/deeplinks/${deeplink.id}`}>
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<Edit2 className="w-4 h-4" />}
            >
              {t('common.edit')}
            </Button>
          </Link>
          <Link to={`/qr-codes?code=${deeplink.code}&platform=${deeplink.platform}&checkNeeded=${deeplink.checkNeeded}`}>
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<QrCode className="w-4 h-4" />}
            >
              QR
            </Button>
          </Link>
          <a
            href={getShortUrlWithProtocol(deeplink)}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:block"
          >
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<ExternalLink className="w-4 h-4" />}
            >
              {t('common.open')}
            </Button>
          </a>
        </div>

        <div className="flex items-center gap-2">
          {onToggleDisabled && deeplink.status !== 'rejected' && (
            <Button
              variant="ghost"
              size="sm"
              className={deeplink.status === 'disabled'
                ? 'text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-dark-600'
              }
              leftIcon={deeplink.status === 'disabled' ? <Power className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
              onClick={() => onToggleDisabled(deeplink.id)}
            >
              {deeplink.status === 'disabled' ? t('deeplinks.enable') : t('deeplinks.disable')}
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
            leftIcon={<Trash2 className="w-4 h-4" />}
            onClick={() => onDelete(deeplink.id)}
          >
            {t('common.delete')}
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default DeeplinkCard;
